import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { CardModel } from "../../../../swagger/api.client.generated";
import { CardShellComponent } from "../card-shell/card-shell.component";
import { CardCategoryIconComponent } from "../card-category-icon/card-category-icon.component";
import { CardContentComponent } from "../card-content/card-content.component";
import {
  BackgroundStyle,
  CardActionEvent,
  ControlOptions,
  SafetyCardCategory,
  UserAttribute,
} from "../card.models";
import { CardLocalService } from "../card.service";
import {
  applyCardEditResult,
  openEditCardDialog,
  processCardContent,
} from "../card-content.utils";
import { isBlankCardName, isMandatoryActivityCard } from "../card-type.utils";
import { AutoOpenCardQueueService } from "../auto-open-card-queue.service";

@Component({
  selector: "app-safety-card",
  templateUrl: "./safety-card.component.html",
  styleUrls: ["./safety-card.component.scss"],
  standalone: true,
  imports: [CommonModule, CardShellComponent, CardCategoryIconComponent, CardContentComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SafetyCardComponent implements OnChanges {
  @Input() cardModel: CardModel | null = null;
  /** Derived from CardVariant — null for legacy Grey cards (no known category). */
  @Input() category: SafetyCardCategory | null = null;
  /** Derived from CardVariant — true for Deck 3 cards (editable). */
  @Input() isCustom: boolean = false;
  @Input() controls: ControlOptions = new ControlOptions();
  @Input() cardSelected: boolean = false;
  @Input() dragDisabled: boolean = false;
  /** ms before a press-and-hold becomes a drag; forwarded to the shell. Default 250. */
  @Input() dragStartDelay: number | { touch: number; mouse: number } = 250;
  @Input() set editMode(v: boolean) { this._editMode = v; }
  protected _editMode: boolean = false;

  @Output() cardAction = new EventEmitter<CardActionEvent>();
  @Output() userAttributes = new EventEmitter<UserAttribute>();

  backgroundStyle: string = "";
  borderCssClass: string = "";
  iconColorClass: string = "";
  cardTypeCss: string = "";
  isEditable: boolean = false;
  flip: "active" | "inactive" = "inactive";
  cardHtml: string = "";
  periodMinutes: number = 0;
  isMediaCard: boolean = false;
  mediaIcon: string = "fas fa-film";

  constructor(
    private dialog: MatDialog,
    private cardLocalService: CardLocalService,
    private cd: ChangeDetectorRef,
    private autoOpenQueue: AutoOpenCardQueueService,
  ) {}

  /** A genuinely-custom card the user hasn't named yet. Gates the create-on-first-edit path
   *  (we only create a custom DB row for a real custom card, not any editable blank card). */
  get isUneditedCustom(): boolean {
    return this.isCustom && isBlankCardName(this.cardModel?.cardName);
  }

  /** Any card still on the blank sentinel name. Drives BOTH the on-card "Custom" placeholder
   *  label and the unsaved-custom styling, so the label and the custom look always appear
   *  together (a "Custom" card must look custom). */
  get isBlankPlaceholder(): boolean {
    return isBlankCardName(this.cardModel?.cardName);
  }

  ngOnChanges(): void {
    if (this.cardModel && this.isCustom && !this.isBlankPlaceholder) {
      this.cardLocalService.registerPlanCard(this.cardModel);
    }
    if (this.cardModel && (this.cardModel as any).autoOpenCreate) {
      (this.cardModel as any).autoOpenCreate = false;
      if (this.isUneditedCustom) {
        this.autoOpenQueue.enqueue(() =>
          this.openEditDialog("edit", {
            autoOpened: true,
            onClosed: () => this.autoOpenQueue.next(),
          })
        );
      }
    }
    const isUnsavedCustom = this.isBlankPlaceholder;
    this.backgroundStyle = isUnsavedCustom ? BackgroundStyle.Custom : BackgroundStyle.Angel;
    this.borderCssClass = this.category ? "sc-" + this.category : "user";
    this.iconColorClass = isUnsavedCustom ? "parchment" : (this.category ?? "");
    this.cardTypeCss = isUnsavedCustom ? "custom-safety" : "";
    // All cards are now editable (previously gated to custom deck-3 cards). Mandatory cards are
    // still restricted to period-only edits, enforced in the edit dialog, not by hiding Edit.
    this.isEditable = true;
    const content = processCardContent(this.cardModel?.cardName ?? "");
    this.isMediaCard = content.isMediaCard;
    this.mediaIcon   = content.mediaIcon;
    this.cardHtml    = content.cardHtml;
    this.periodMinutes = this.cardModel?.periodMinutes ?? 0;
  }

  flipCard() {
    this.flip = this.flip === "inactive" ? "active" : "inactive";
    this.cardLocalService.cardFlipSound.play();
    this.cd.markForCheck();
  }

  onShellCardAction(event: CardActionEvent): void {
    if (event.action === "create" || event.action === "edit") {
      this.openEditDialog("edit");
    } else if (event.action === "duplicate") {
      this.openEditDialog("duplicate");
    } else {
      this.cardAction.emit(event);
    }
  }

  openEditDialog(
    mode: "edit" | "duplicate" = "edit",
    options?: { autoOpened?: boolean; onClosed?: () => void }
  ): void {
    if (!this.cardModel) {
      options?.onClosed?.();
      return;
    }

    const card = this.cardModel;
    // Brand-new custom placeholder = a create (not an edit): keep the CreateCustomCard path.
    const wasBrandNewCustom = this.isUneditedCustom;
    const isTracked = this.cardLocalService.hasLocalCustomCard(card.cardGuid);
    const isMandatory = isMandatoryActivityCard(card);

    openEditCardDialog(
      this.dialog,
      card,
      mode,
      this.cardLocalService.getDeckSnapshot(),
      isMandatory,
      { cardKind: "safety", safetyCategory: this.category, isCreate: wasBrandNewCustom }
    ).subscribe({
      next: (result) => {
      if (!result || !this.cardModel) {
        // Cancelling a dialog we auto-opened (not one the user opened manually) drops the
        // just-added blank card from the plan, so nothing is left half-created.
        if (options?.autoOpened && this.cardModel) {
          this.cardAction.emit({ action: "remove", card: this.cardModel });
        }
        return;
      }

      if (wasBrandNewCustom) {
        // Outside an active plan edit, this.cardModel is the category's shared template
        // reference sitting in CardLocalService.cards[] (the picker reads it live). Editing it
        // in place would consume the template, leaving that category with no create affordance.
        // Clone instead, so the template stays in the cache untouched; a plan-in-progress slot
        // has no such shared reference to protect, so it's still filled in place as before.
        if (!this._editMode) {
          // Card Library context (deck-management dialog): no plan-save will run, so persist and
          // refetch the deck. createLibraryCustomCard makes the card server truth so it survives plan
          // navigation (clearLocalCustomCards) and a page refresh, and surfaces failures instead of
          // the old swallow-and-fake-success behaviour.
          //
          // Flag the clone as user-defined so resolveCardVariant keeps isCustom=true after the
          // rename, once isEditableTemplate is cleared post-creation. Do NOT tag cardDeckId=3 here:
          // a Feelings card is safety category 1, which collides with the activity-recommended
          // category id (1) — a deck-3 + category-1 card resolves to Activity, so a new custom
          // Feelings card would flash as an activity card until the next server fetch returns it as
          // deck 2. Leaving the Safety deckType (cardDeckType.cardDeckId=2) intact resolves it
          // correctly, and isUserDefined keeps it flagged custom.
          this.cardModel = Object.assign(new CardModel(), this.cardModel, {
            isUserDefined: true,
            isPatientFavourite: false,
          });
          this.applyLocalEdit(result);
          // Pass deckTypeId=2 explicitly: SafetyCardComponent always creates safety cards.
          this.cardLocalService.createLibraryCustomCard(this.cardModel, 2).subscribe({
            next: (cardId) => {
              this.cardModel!.cardId = cardId;
              this.cardModel!.isEditableTemplate = false;
              this.cd.markForCheck();
              this.cardAction.emit({ action: "create", card: this.cardModel });
            },
            // On error the service surfaces a snackbar; don't fake success.
          });
          this.cd.markForCheck();
          return;
        }
        this.applyLocalEdit(result);
        // A newly created card is never favourited server-side, regardless of whether the shared
        // template it was cloned from happens to be.
        this.cardModel!.isPatientFavourite = false;
        // In-plan create: persist immediately and clear isEditableTemplate so the plan save
        // (triggered by the parent's editFinished) does not treat this as a pending template and
        // create a second duplicate card. Pass deckTypeId=2 explicitly: SafetyCardComponent always
        // creates safety cards. Without this, deck-3 template cards send cardDeckId=3, which
        // CreateUserCard treats as Activity.
        this.cardLocalService.createCustomCardInDb(this.cardModel, 2).subscribe({
          next: (cardId) => {
            this.cardModel!.cardId = cardId;
            this.cardModel!.isEditableTemplate = false;
            this.cardLocalService.upsertLocalCustomCard(this.cardModel!);
            this.cd.markForCheck();
            this.cardAction.emit({ action: "create", card: this.cardModel });
          },
          error: () => {
            this.cardLocalService.upsertLocalCustomCard(this.cardModel!);
            this.cardAction.emit({ action: "create", card: this.cardModel });
          },
        });
        this.cd.markForCheck();
        return;
      }

      // Existing saved card: the backend decides in-place / new-version / copy-on-write / override.
      if (mode === "duplicate" && this._editMode) {
        // In plan edit mode: wait for the response so we can emit the new card (new cardId)
        // up to PlanCardActionService, which will add it to the plan.
        this.cardLocalService.editCard(this.cardModel, result).subscribe((response) => {
          if (response && !response.validationError && response.createdNewCard && response.cardId) {
            const duplicatedCard = Object.assign(new CardModel(), this.cardModel, {
              cardId: response.cardId,
              cardName: result.cardName,
              periodMinutes: result.periodMinutes ?? this.cardModel?.periodMinutes,
              cardDescription: result.description,
              cardSessionCardId: 0,
              // A duplicate is a genuinely new deck entry (matches the server, which always
              // creates it unfavourited) — it must not inherit the original's favourite status.
              isPatientFavourite: false,
            });
            this.cardAction.emit({ action: "duplicate", card: duplicatedCard });
          }
          this.cd.markForCheck();
        });
      } else {
        this.cardLocalService.editCard(this.cardModel, result).subscribe((response) => {
          // Reflect a direct edit on the in-plan instance immediately. Duplicate leaves the
          // original untouched (the copy surfaces via the deck refresh).
          if (mode === "edit" && this._editMode) {
            this.applyLocalEdit(result);
            // A DirectEdit that the backend resolved as copy-on-write creates a new real card —
            // repoint this slot at it (and force a fresh CardSessionCard on next save), otherwise
            // the plan keeps saving against the old, now-deactivated card.
            if (response && !response.validationError && response.createdNewCard && response.cardId) {
              this.cardModel!.cardId = response.cardId;
              this.cardModel!.cardSessionCardId = 0;
            }
            if (isTracked) this.cardLocalService.upsertLocalCustomCard(this.cardModel!);
          }

          this.cd.markForCheck();
          this.cardAction.emit({ action: mode, card: this.cardModel });
        });
      }
      },
      complete: () => options?.onClosed?.(),
    });
  }

  private applyLocalEdit(result: { cardName: string; periodMinutes?: number; description?: string }): void {
    if (!this.cardModel) return;
    this.cardHtml = applyCardEditResult(this.cardModel, result);
    this.periodMinutes = this.cardModel.periodMinutes;
    // Post-edit: safety cards always restore to Angel background.
    // cardModel is mutated in-place so ngOnChanges won't fire; update derived state manually.
    this.backgroundStyle = BackgroundStyle.Angel;
    // Mirror ngOnChanges: a named safety card keeps its category colour. Blanking this dropped the
    // category class (e.g. "feelings") off the icon after an in-plan edit, and it only came back if
    // a fresh cardModel reference happened to re-trigger ngOnChanges.
    this.iconColorClass = this.category ?? "";
    this.cardTypeCss = "";
  }

}
